import dayjs from 'dayjs';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { SubmittalSnapshotData } from 'src/app/shared/models/offers.model';
import { DateHelpers } from 'src/app/shared/helpers';

export type SnapshotDiffKey =
    | 'contractLength'
    | 'jobId'
    | 'facilityRate'
    | 'ayaPaidBonus'
    | 'targetMargin'
    | 'specialty'
    | 'startDate'
    | 'shiftType'
    | 'shifts'
    | 'shiftLength'
    | 'shiftTimeStart'
    | 'shiftTimeEnd'
    | 'rto'
    | 'shiftsAlt'
    | 'shiftAltLength'
    | 'shiftAltTimeStart'
    | 'shiftAltTimeEnd';

export interface SnapshotDiffMap {
    readonly contractLength: number | null;
    readonly jobId: number | null;
    readonly facilityRate: number | null;
    readonly ayaPaidBonus: number | null;
    readonly targetMargin: number | null;
    readonly specialty: string | null;
    readonly startDate: Date | null;
    readonly shiftType: number | null;
    readonly shifts: number | null;
    readonly shiftLength: number | null;
    readonly shiftTimeStart: string | null;
    readonly shiftTimeEnd: string | null;
    readonly rto: Date[] | null;
    readonly shiftsAlt: number | null;
    readonly shiftAltLength: number | null;
    readonly shiftAltTimeStart: string | null;
    readonly shiftAltTimeEnd: string | null;
}

export const SnapshotDiffKeyLabel = [
    { key: 'contractLength', label: 'Contract Length' },
    { key: 'jobId', label: 'Job ID' },
    { key: 'facilityRate', label: 'Facility Rate' },
    { key: 'ayaPaidBonus', label: 'AYA Paid Bonus' },
    { key: 'targetMargin', label: 'Target Margin' },
    { key: 'specialty', label: 'Specialty' },
    { key: 'startDate', label: 'Start Date' },
    { key: 'shiftType', label: 'Shift Type' },
    { key: 'shifts', label: 'Shifts' },
    { key: 'shiftLength', label: 'Shift Length' },
    { key: 'shiftTimeStart', label: 'Shift Time Start' },
    { key: 'shiftTimeEnd', label: 'Shift Time End' },
    { key: 'rto', label: 'RTO' },
    { key: 'shiftsAlt', label: 'Shifts Alt' },
    { key: 'shiftAltLength', label: 'Shift Alt Length' },
    { key: 'shiftAltTimeStart', label: 'Shift Alt Time Start' },
    { key: 'shiftAltTimeEnd', label: 'Shift Alt Time End' }
];

export const emptySnapshotDiffMap: SnapshotDiffMap = {
    contractLength: null,
    jobId: null,
    facilityRate: null,
    ayaPaidBonus: null,
    targetMargin: null,
    specialty: null,
    startDate: null,
    shiftType: null,
    shifts: null,
    shiftLength: null,
    shiftTimeStart: null,
    shiftTimeEnd: null,
    rto: null,
    shiftsAlt: null,
    shiftAltLength: null,
    shiftAltTimeStart: null,
    shiftAltTimeEnd: null
};

export function buildSnapshotDiffs(
    snapshot: SubmittalSnapshotData | null | undefined,
    form: UntypedFormGroup | null | undefined
): SnapshotDiffMap {
    if (!snapshot?.jobDetails || !form) {
        return emptySnapshotDiffMap;
    }

    const { jobDetails } = snapshot;
    const { shiftDetails } = jobDetails;

    const createTime = (hours?: number | null, minutes?: number | null): string | null => {
        if (hours == null || minutes == null) {
            return null;
        }
        return dayjs().set('hour', hours).set('minute', minutes).set('second', 0).set('millisecond', 0).format('HH:mm');
    };

    return {
        contractLength: jobDetails.length ?? null,
        jobId: jobDetails.jobId ?? null,
        facilityRate: jobDetails.billRate ?? null,
        ayaPaidBonus: jobDetails.ayaPaidBonus ?? null,
        targetMargin: jobDetails.targetMargin ?? null,
        specialty: jobDetails.specialty ?? null,
        startDate: jobDetails.startDate ? dayjs(jobDetails.startDate).startOf('day').toDate() : null,
        shiftType: shiftDetails?.shiftTypeId ?? null,
        shifts: shiftDetails?.shifts ?? null,
        shiftLength: shiftDetails?.hours ?? null,
        shiftTimeStart: createTime(shiftDetails?.shiftH, shiftDetails?.shiftM),
        shiftTimeEnd: createTime(shiftDetails?.shiftToH, shiftDetails?.shiftToM),
        rto: snapshot.candidateRequestedTimeOff ?? null,
        shiftsAlt: shiftDetails?.shiftsAlt ?? null,
        shiftAltLength: shiftDetails?.hoursAlt ?? null,
        shiftAltTimeStart: createTime(shiftDetails?.shiftAltH, shiftDetails?.shiftAltM),
        shiftAltTimeEnd: createTime(shiftDetails?.shiftAltToH, shiftDetails?.shiftAltToM)
    };
}

export function isOfferSnapshotDifferent<T>(
    diffs: SnapshotDiffMap,
    key: SnapshotDiffKey,
    current: T | null | undefined,
    compare: (currentValue: T, snapshotValue: T) => boolean = (curr, snap) => curr === snap
): boolean {
    const snapshotValue = diffs[key] as T | null;
    if (snapshotValue == null) {
        return false;
    }
    if (current == null) {
        return true;
    }
    return !compare(current, snapshotValue);
}

export function normalizeNumberControl(control: AbstractControl | null | undefined): number | null {
    const raw = Number(control?.getRawValue());
    return Number.isNaN(raw) ? null : raw;
}

export function normalizeDayControl(control: AbstractControl | null | undefined): Date | null {
    const raw: Date | null | undefined = control?.getRawValue();
    return raw ? dayjs(raw).startOf('day').toDate() : null;
}

export function normalizeTimeControl(control: AbstractControl | null | undefined): string | null {
    const raw: Date | null | undefined = control?.getRawValue();
    if (!raw || Number.isNaN(raw.getTime())) {
        return null;
    }
    return dayjs(raw).format('HH:mm');
}

export function areRtoListsEqual(current: Date[] | null | undefined, snapshot: Date[] | null | undefined): boolean {
    return DateHelpers.areDateListsEqual(current ?? [], snapshot ?? []);
}
